import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Leaf, GitMerge, Landmark, Building } from 'lucide-react';
import SEO from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const CaseStudiesPage = () => {
  const { locale } = useParams();

  const caseStudies = [
    {
      id: 1,
      title: 'End-to-End PMC for a ₹250 Cr Oral Solid Dosage Facility Expansion',
      category: 'Pharma Engineering',
      icon: Building2,
      result: 'Delivered on-time and 8% under budget with zero regulatory observations',
      image: '/images/external/photo-1587854692152-cbe660dbde88.jpg'
    },
    {
      id: 2,
      title: 'BRSR Compliance Delivered in 60 Days for a NSE-Listed Manufacturer',
      category: 'ESG & Sustainability',
      icon: Leaf,
      result: 'First-time BRSR filing with 95% disclosure completeness score',
      image: '/images/external/photo-1497436072909-60f360e1d4b1.jpg'
    },
    {
      id: 3,
      title: 'Cross-Border Acquisition Advisory for a Mid-Cap Pharma Group',
      category: 'M&A',
      icon: GitMerge,
      result: 'Successfully closed $45M acquisition with 15% below initial valuation',
      image: '/images/external/photo-1560472355-536de3962603.jpg'
    },
    {
      id: 4,
      title: 'Project Finance Structuring for 500,000 sq ft Industrial Park',
      category: 'Finance',
      icon: Landmark,
      result: 'Secured ₹180 Cr term loan at competitive rates within 45 days',
      image: '/images/external/photo-1554224155-8d04cb21cd6c.jpg'
    },
    {
      id: 5,
      title: 'Cross-Border Investment Sale of a ₹1,200 Cr Grade-A Logistics Portfolio',
      category: 'Capital Markets',
      icon: Building,
      result: 'Closed sale to global institutional buyer at 6.2% cap rate within 9 months',
      image: '/images/external/photo-1486406146926-c627a92ad1ab.jpg'
    },
    {
      id: 6,
      title: 'cGMP Gap Assessment and Remediation for Export-Oriented API Plant',
      category: 'Pharma Engineering',
      icon: Building2,
      result: 'Achieved WHO cGMP certification within 6 months of engagement',
      image: '/images/external/photo-1532187863486-abf9dbad1b69.jpg'
    },
  ];

  return (
    <div className="pt-20" data-testid="case-studies-page">
      <SEO 
        title="Case Studies"
        description="Real-world examples of how H&G delivers measurable value across pharma engineering, ESG, finance, capital markets, and M&A consulting."
        path="/case-studies"
      />
      {/* Hero */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-white via-light-grey to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-teal font-medium text-sm uppercase tracking-wider">Proven Results</span>
            <h1 className="font-playfair text-4xl md:text-5xl text-navy mt-4 mb-4">
              Case Studies
            </h1>
            <p className="text-lg text-mid-grey">
              Real-world examples of how H&G delivers measurable value across all five practice areas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {caseStudies.map((study) => {
              const IconComponent = study.icon;
              return (
                <motion.div
                  key={study.id}
                  variants={fadeInUp}
                  className="case-study-card bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
                >
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center px-3 py-1 bg-white/90 backdrop-blur-sm text-teal rounded-full text-xs font-medium">
                        <IconComponent className="w-3 h-3 mr-1" />
                        {study.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-playfair text-lg text-navy mb-3">
                      {study.title}
                    </h3>
                    <div className="bg-teal/10 p-3 rounded-lg mb-4">
                      <p className="text-sm text-navy font-medium">
                        Result: {study.result}
                      </p>
                    </div>
                    <button className="inline-flex items-center text-teal font-medium text-sm hover:text-teal transition-colors">
                      Read Case Study <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">
            Ready to Create Your Success Story?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Let's discuss how H&G can deliver similar results for your organization.
          </p>
          <Link to={`/${locale}/contact`} className="btn-primary">
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
